import {
  ANDROID_PACKAGE,
  APP_SCHEME,
} from '../constant/appLinks';

const getHost = (): string =>
  typeof window !== 'undefined' && window.location.host
    ? window.location.host
    : 'sporti.app';

const normalizePathname = (pathname: string): string =>
  pathname.startsWith('/') ? pathname : `/${pathname}`;

type OpenAndroidOptions = {
  /** When true, Chrome opens Play Store if the app cannot handle the intent. */
  includeStoreFallback?: boolean;
  playStoreUrl?: string;
};

/**
 * Opens the native app on Android. Tries HTTPS App Link intent first (matches
 * React Navigation prefixes like https://sporti.app), then sporti:// scheme.
 */
export const openAndroidApp = (
  pathname: string,
  playStoreUrl: string,
  options: OpenAndroidOptions = {},
): void => {
  const { includeStoreFallback = false } = options;
  const host = getHost();
  const path = normalizePathname(pathname);

  const fallback =
    includeStoreFallback && playStoreUrl
      ? `;S.browser_fallback_url=${encodeURIComponent(playStoreUrl)}`
      : '';

  // 1) HTTPS intent — same shape as Linking prefixes in the mobile app
  const httpsIntent = `intent://${host}${path}#Intent;scheme=https;package=${ANDROID_PACKAGE}${fallback};end`;

  window.location.href = httpsIntent;
};

/**
 * Opens via custom scheme (sporti://) — backup when HTTPS intent does not fire.
 */
export const openAndroidAppCustomScheme = (pathname: string): void => {
  const path = normalizePathname(pathname);
  const pathWithoutLeading = path.replace(/^\//, '');
  window.location.href = `${APP_SCHEME}://${pathWithoutLeading}`;
};

/**
 * Opens the native app on iOS. Uses sporti:// so React Navigation can match
 * paths like provider/unclaimed-profile (same as sporti:// prefix in linking.ts).
 */
export const openIOSApp = (pathname: string, search = '', hash = ''): void => {
  const path = normalizePathname(pathname);
  const pathWithoutLeading = path.replace(/^\//, '');
  const query = `${search}${hash}`;
  window.location.href = `${APP_SCHEME}://${pathWithoutLeading}${query}`;
};

/** Full https URL the app registers (for display / copy). */
export const getAppDeepLinkUrl = (pathname: string): string => {
  const host = getHost();
  return `https://${host}${normalizePathname(pathname)}`;
};
